import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./translations/en/common.json";
import es from "./translations/es/common.json";

// The two languages the app actually ships. supportedLngs keeps a browser set
// to some third language rendering in DEFAULT_LANG rather than showing raw keys.
export const SUPPORTED_LANGS = ["en", "es"];

// Where a visitor lands when they have not picked a language themselves.
// Spanish, deliberately: anything that is not clearly English gets Spanish,
// which keeps the Spanish-first posture for a browser set to some third
// language. Set to "en" to flip that.
export const DEFAULT_LANG = "es";

// "es-MX" -> "es", "en-GB" -> "en". Exported so the regression test can pin it.
export const normalizeLanguage = (lng) => String(lng).split("-")[0].toLowerCase();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGS,
    load: "languageOnly",
    debug: false,
    resources: {
      en: { common: en },
      es: { common: es }
    },
    ns: ["common"],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      // ?lng= first: an explicit language in the URL is the strongest statement
      // of intent there is, and it has to beat a cached value or campaign links
      // to the Spanish landing page silently render English. This ordering
      // matters more now than it used to — "navigator" writes a cached value on
      // the very first pageview, where before only the EN/ES toggle ever did,
      // so localStorage-first would have left ?lng= dead for almost everyone.
      // Then localStorage, so a toggle choice sticks across visits. Then the
      // visitor's own browser/OS language: this is what makes the app
      // location-aware in the way that actually serves the audience — a phone
      // set to Spanish gets Spanish wherever it is, including in the US, where
      // a geo-IP lookup would have wrongly handed it English.
      // A browser that is neither es nor en falls through to DEFAULT_LANG.
      order: ["querystring", "localStorage", "navigator"],
      caches: ["localStorage"],
      // Browsers report regional tags ("es-MX", "es-419", "en-GB"). Collapse
      // them to the base code BEFORE i18next stores it, so i18n.language is
      // always exactly "en" or "es". This is load-bearing, not cosmetic:
      // LearningHubComponent and LearningHubComponent1 pass i18n.language
      // straight through as the backend content locale, and that value is the
      // lesson cache key — an "es-MX" reaching it would miss the cache and
      // regenerate the lesson from Claude. AiAssistant compares it with ===.
      // supportedLngs alone does NOT do this: it normalizes resolvedLanguage
      // only and leaves i18n.language as the raw regional tag.
      convertDetectedLanguage: normalizeLanguage
    }
  });

// Keep <html lang> in step with the active language.
//
// index.html hardcodes lang="en" and nothing ever changed it, while
// DEFAULT_LANG is "es" — so the MAJORITY case was Spanish content served under
// lang="en", and a screen reader read it with English phonetics: largely
// unintelligible. App-wide, but it bites hardest on /contact, which is where a
// Spanish speaker lands straight off LandingPage11's "Hablemos". WCAG 3.1.1.
const syncDocumentLang = (lng) => {
  if (typeof document === "undefined") return;   // non-DOM contexts import this too
  const normalized = normalizeLanguage(lng || DEFAULT_LANG);
  document.documentElement.lang = SUPPORTED_LANGS.includes(normalized)
    ? normalized
    : DEFAULT_LANG;
};

syncDocumentLang(i18n.language);
i18n.on("languageChanged", syncDocumentLang);

export default i18n;
